import { ArrowUpDown, Grid3X3, List } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface FolderViewControllerProps {
  viewMode: "grid" | "list";
  sortOrder: "asc" | "desc";
  sortBy: "name" | "size";
  onSort: (by: "name" | "size") => void;
  onViewModeChange: (mode: "grid" | "list") => void;
  children?: ReactNode;
}

export const FolderViewController: React.FC<FolderViewControllerProps> = ({
  viewMode,
  sortOrder,
  sortBy,
  onSort,
  onViewModeChange,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-4">
      {children}
      {/* Sort Buttons */}
      <div className="flex space-x-2">
        <button
          onClick={() => onSort("name")}
          className={`flex items-center space-x-2 rounded-lg border border-gray-300 px-3 py-2 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700 ${
            sortBy === "name" ? "bg-gray-100 dark:bg-navy-800" : ""
          }`}
        >
          <span className="dark:text-white">
            {t("library.sortByName")}{" "}
            {sortBy === "name" ? (sortOrder === "asc" ? t("library.sortAscending") : t("library.sortDescending")) : ""}
          </span>
          <ArrowUpDown className="h-4 w-4 dark:text-white" />
        </button>
        <button
          onClick={() => onSort("size")}
          className={`flex items-center space-x-2 rounded-lg border border-gray-300 px-3 py-2 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700 ${
            sortBy === "size" ? "bg-gray-100 dark:bg-navy-800" : ""
          }`}
        >
          <span className="dark:text-white">
            {t("library.sortBySize")} {sortBy === "size" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
          </span>
          <ArrowUpDown className="h-4 w-4 dark:text-white" />
        </button>
      </div>

      {/* View Toggle */}
      <div className="flex rounded-lg bg-gray-100 p-1 dark:bg-navy-700">
        <button
          onClick={() => onViewModeChange("list")}
          className={`rounded p-2 ${
            viewMode === "list"
              ? "bg-white shadow-sm dark:bg-navy-800"
              : "hover:bg-gray-200 dark:hover:bg-navy-600"
          }`}
          aria-label={t("library.listView")}
        >
          <List className="h-4 w-4 dark:text-white" />
        </button>
        <button
          onClick={() => onViewModeChange("grid")}
          className={`rounded p-2 ${
            viewMode === "grid"
              ? "bg-white shadow-sm dark:bg-navy-800"
              : "hover:bg-gray-200 dark:hover:bg-navy-600"
          }`}
          aria-label={t("library.gridView")}
        >
          <Grid3X3 className="h-4 w-4 dark:text-white" />
        </button>
      </div>
    </div>
  );
};
